import Splide from '@splidejs/splide';
import { clientLoadMore } from '../util/loadMore';
import showMenuOnScrollUp from '../partials/navbar';
import { trapFocusOnModal } from '../util/modalFocusTrap';
import { handleCookiesSettingsUpdate, handleGDPRModal, handleYoutubeGDPR } from '../partials/gdpr';

export default {
  init() {
    // JavaScript to be fired on all pages
    const delayNavbarMouseOverClose = () => {
      const menuItems = document.querySelectorAll('li.has-submenu');
      menuItems.forEach((el) => {
        let timer;
        el.addEventListener('mouseout', function (event) {
          const currentTarget = event.currentTarget;
          timer = setTimeout(function () {
            currentTarget.classList.remove('open');
            currentTarget.querySelector('a').setAttribute('aria-expanded', 'false');
          }, 500);
        });
        el.addEventListener('mouseover', function () {
          this.classList.add('open');
          this.parentNode.querySelector('a').setAttribute('aria-expanded', 'true');
          clearTimeout(timer);
        });
        el.querySelector('button').addEventListener('click', function (event) {
          if (this.parentNode.classList.contains('has-submenu')) {
            menuItems.forEach((menuItem) => menuItem.classList.remove('open'));
            this.parentNode.classList.add('open');
            this.parentNode.querySelector('a').setAttribute('aria-expanded', 'true');
            this.parentNode.querySelector('button').setAttribute('aria-expanded', 'true');
          } else {
            this.parentNode.classList.remove('open');
            this.parentNode.querySelector('a').setAttribute('aria-expanded', 'false');
            this.parentNode.querySelector('button').setAttribute('aria-expanded', 'false');
          }
          event.preventDefault();
        });
      });
    };
    delayNavbarMouseOverClose();

    const outlineMenuItemOnKeyboardFocus = () => {
      const hiddenMenuItems = document.querySelectorAll('button.visually-hidden');
      hiddenMenuItems.forEach((hiddenMenuItem) => {
        const submenu = hiddenMenuItem.nextElementSibling;
        hiddenMenuItem.addEventListener('focus', (event) => {
          event.target.previousElementSibling.classList.add('focus-ring');
        });
        hiddenMenuItem.addEventListener('blur', (event) => {
          event.target.previousElementSibling.classList.remove('focus-ring');
        });
        submenu.addEventListener('blur', () => (this.style.visibility = 'hidden'));
      });
    };
    outlineMenuItemOnKeyboardFocus();

    const closeSubmenuOnLastItemBlur = () => {
      const menuItems = document.querySelectorAll('li.has-submenu');
      menuItems.forEach((menuItem) => {
        const lastSubmenuItem = menuItem.querySelector('li:last-of-type a');
        lastSubmenuItem.addEventListener('blur', () => {
          menuItem.classList.remove('open');
        });
      });
    };

    closeSubmenuOnLastItemBlur();

    const handleExpandableSearchBar = () => {
      const wrappers = document.querySelectorAll('.expandable-search');
      const toggleSearchBar = (e, wrapper) => {
        e.stopPropagation();
        const inputField = wrapper.querySelector('.search-input');
        const submitButton = wrapper.querySelector('button');
        wrapper.classList.add('search-open');
        inputField.classList.remove('hidden');
        submitButton.classList.remove('!hidden');
        inputField.setAttribute('aria-hidden', 'false');
        submitButton.setAttribute('aria-hidden', 'false');
        inputField.focus();
        if (wrapper.classList.contains('search-open')) {
          document.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            wrapper.classList.remove('search-open');
            inputField.classList.add('hidden');
            submitButton.classList.add('!hidden');
            inputField.setAttribute('aria-hidden', 'true');
            submitButton.setAttribute('aria-hidden', 'true');
          });
        }
      };
      wrappers.forEach((wrapper) => {
        const submitBtn = wrapper.querySelector('.search-submit');
        wrapper.addEventListener('keypress', (e) => {
          toggleSearchBar(e, wrapper);
        });
        wrapper.addEventListener('click', (e) => {
          toggleSearchBar(e, wrapper);
        });
        submitBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (e.currentTarget.previousElementSibling.value.length < 2) e.preventDefault();
        });
      });
    };

    handleExpandableSearchBar();

    const handlePrintModal = () => {
      const trigger = document.getElementById('print-modal-button');
      const modal = document.getElementById('print-modal');
      if (trigger && modal) {
        trapFocusOnModal({ modal, trigger });
      }
    };

    handlePrintModal();

    const toggleShareModal = ({ modal, trigger }) => {
      trigger.addEventListener('click', () => {
        const title = trigger.getAttribute('data-title');
        const url = trigger.getAttribute('data-url');
        const shareButtons = document.getElementById('share-buttons');
        shareButtons.querySelectorAll('a').forEach((link) => {
          const defaultHref = link.getAttribute('data-default-href');
          const hrefWithCorrectUrl = defaultHref.replaceAll('%5BURL%5D', url).replaceAll('%5BTEXT%5D', title);
          link.href = hrefWithCorrectUrl;
        });
      });
      trapFocusOnModal({ modal, trigger });
    };

    const handleShareModal = () => {
      const triggers = document.querySelectorAll('.js-share-button');
      const modal = document.getElementById('share-modal');
      if (triggers.length > 0 && modal) {
        triggers.forEach((trigger) => {
          toggleShareModal({ modal, trigger });
        });
      }
    };
    handleShareModal();

    const handleNewsletterModal = () => {
      const triggers = document.querySelectorAll('.js-newsletter-button');
      const modal = document.getElementById('newsletter-modal');
      const form = document.getElementById('CF5a12d00449cca_1');
      if (triggers.length > 0 && modal) {
        triggers.forEach((trigger) => {
          trapFocusOnModal({ modal, trigger });
          trigger.addEventListener('click', (e) => {
            const temporaryEmailFieldValue = e.currentTarget.previousElementSibling.value;
            const realFormEmailField = form.querySelector('input[type="email"]');
            realFormEmailField.value = temporaryEmailFieldValue;
          });
        });
      }
    };
    handleNewsletterModal();

    const handleEmailConfirmationModal = () => {
      const modal = document.getElementById('email-confirm-modal');
      trapFocusOnModal({ modal });
    };
    const isAnEmailValidationLink = new URL(window.location).searchParams.get('validatetoken');

    if (isAnEmailValidationLink) {
      handleEmailConfirmationModal();
    }

    const handleSearchModal = () => {
      const triggers = document.querySelectorAll('.search-modal-trigger');
      const modal = document.getElementById('search-modal');
      if (triggers.length > 0 && modal) {
        triggers.forEach((trigger) => {
          trapFocusOnModal({ modal, trigger });
          trigger.addEventListener('click', (e) => {
            const modalTitle = e.currentTarget.getAttribute('data-title');
            const searchEngine = e.currentTarget.getAttribute('data-engine');
            const form = modal.querySelector('form');
            if (form) {
              const searchInput = form.querySelector('input');
              const label = form.querySelector('label');
              modal.querySelector('label').innerHTML = modalTitle;
              modal.ariaLabel = modalTitle;
              form.action = '/recherche';
              searchInput.name = searchEngine;
              searchInput.placeholder = modalTitle;
              label.innerHTML = modalTitle;
            }
          });
        });
      }
    };
    handleSearchModal();

    const softLoadMoreBtn = document.getElementById('js-soft-load-more');
    if (softLoadMoreBtn) {
      softLoadMoreBtn.addEventListener('click', () => {
        const increment = parseInt(softLoadMoreBtn.getAttribute('data-increment'));
        const listId = softLoadMoreBtn.getAttribute('data-list-id') || 'js-results';
        const itemClass = softLoadMoreBtn.getAttribute('data-item-class') || 'js-result-item';
        clientLoadMore({ button: softLoadMoreBtn, increment, listId, itemClass });
      });
    }

    showMenuOnScrollUp();
    handleYoutubeGDPR();
    handleGDPRModal();
    handleCookiesSettingsUpdate();
  },
  finalize() {
    // JavaScript to be fired on all pages, after page specific JS is fired
    if (document.getElementById('actualites-slider')) {
      new Splide('#actualites-slider', {
        pagination: false,
        fixedWidth: '512px',
        focus: 'center',
        perMove: 1,
        gap: '2rem',
        padding: '1rem',
        keyboard: 'focused',
        breakpoints: { 500: { fixedWidth: '300px', perMove: 1, focus: false } },
      }).mount();
    }
    if (document.getElementById('reseaux-slider')) {
      new Splide('#reseaux-slider', {
        pagination: false,
        fixedWidth: '270px',
        perMove: 1,
        gap: '2rem',
        breakpoints: { 500: { focus: 'center' } },
      }).mount();
    }
    if (document.getElementById('representants-slider')) {
      new Splide('#representants-slider', {
        pagination: false,
        perMove: 1,
      }).mount();
    }
    if (document.getElementById('thematiques-slider')) {
      new Splide('#thematiques-slider', {
        pagination: false,
        autoWidth: true,
        gap: '1rem',
        keyboard: 'focused',
        breakpoints: { 500: { arrows: false, perMove: 1 } },
      }).mount();
    }
  },
};
